import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { fr } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

// French date widgets (jj/mm/aaaa, hh:mm) with the same value/onChange contract as native inputs, identical on every browser.
const BOX = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
const pad = (n) => String(n).padStart(2, "0");
const isoToFr = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
export const frToIso = (t) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(t || "");
  if (!m) return null;
  const dt = new Date(+m[3], +m[2] - 1, +m[1]);
  return dt.getMonth() === +m[2] - 1 && dt.getDate() === +m[1] ? `${m[3]}-${m[2]}-${m[1]}` : null;
};
const maskDate = (raw) => { const d = raw.replace(/\D/g, "").slice(0, 8); return [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join("/"); };
const maskTime = (raw) => { const d = raw.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d; };
const validTime = (t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
const emit = (onChange, value) => onChange?.({ target: { value } });

const DatePart = ({ iso, onIso, testid, required, disabled }) => {
  const [text, setText] = useState(isoToFr(iso));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(isoToFr(iso)); }, [iso]);
  const change = (raw) => {
    const t = maskDate(raw);
    setText(t);
    if (!t) onIso("");
    else if (frToIso(t)) onIso(frToIso(t));
  };
  const sel = iso ? new Date(`${iso.slice(0, 10)}T12:00:00`) : undefined;
  return (
    <div className="relative w-full">
      <input inputMode="numeric" placeholder="jj/mm/aaaa" value={text} onChange={(e) => change(e.target.value)}
        onBlur={() => { if (text && !frToIso(text)) setText(isoToFr(iso)); }} required={required} disabled={disabled}
        className={cn(BOX, "pr-9")} data-testid={testid} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Choisir une date" className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 hover:text-cyan-300"
            data-testid={testid ? `${testid}-calendar` : undefined}><CalendarDays className="h-4 w-4" /></button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="single" locale={fr} weekStartsOn={1} selected={sel} defaultMonth={sel} initialFocus
            onSelect={(d) => { if (d) { onIso(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`); setOpen(false); } }} />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const TimePart = ({ value, onValue, testid, required, disabled }) => {
  const [t, setT] = useState(value);
  useEffect(() => { setT(value); }, [value]);
  return (
    <input inputMode="numeric" placeholder="hh:mm" value={t} required={required} disabled={disabled} className={BOX} data-testid={testid}
      onChange={(e) => { const m = maskTime(e.target.value); setT(m); if (!m) onValue(""); else if (validTime(m)) onValue(m); }}
      onBlur={() => { if (t && !validTime(t)) setT(value); }} />
  );
};

const MonthPart = ({ value, onValue, testid, disabled }) => {
  const toFr = (v) => (v ? `${v.slice(5, 7)}/${v.slice(0, 4)}` : "");
  const [t, setT] = useState(toFr(value));
  useEffect(() => { setT(toFr(value)); }, [value]);
  return (
    <input inputMode="numeric" placeholder="mm/aaaa" value={t} disabled={disabled} className={BOX} data-testid={testid}
      onChange={(e) => {
        const d = e.target.value.replace(/\D/g, "").slice(0, 6);
        const m = d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
        setT(m);
        if (!m) onValue("");
        else if (/^(0[1-9]|1[0-2])\/\d{4}$/.test(m)) onValue(`${m.slice(3)}-${m.slice(0, 2)}`);
      }}
      onBlur={() => { if (t && !/^(0[1-9]|1[0-2])\/\d{4}$/.test(t)) setT(toFr(value)); }} />
  );
};

const DateTimePart = ({ value, onChange, testid, required, disabled }) => {
  const [d, tRaw] = (value || "").split("T");
  const [pendingDate, setPendingDate] = useState(d || "");
  const [pendingTime, setPendingTime] = useState((tRaw || "").slice(0, 5));
  useEffect(() => { if (value) { setPendingDate(d); setPendingTime((tRaw || "").slice(0, 5)); } }, [value, d, tRaw]);
  // Like a native datetime-local: a value is emitted only when both the date and the time are complete.
  const combine = (date, time) => emit(onChange, date && time ? `${date}T${time}` : "");
  return (
    <div className="flex w-full gap-2">
      <DatePart iso={pendingDate} onIso={(x) => { setPendingDate(x); combine(x, pendingTime); }} testid={testid} required={required} disabled={disabled} />
      <div className="w-[4.75rem] shrink-0">
        <TimePart value={pendingTime} onValue={(x) => { setPendingTime(x); combine(pendingDate, x); }} testid={testid ? `${testid}-time` : undefined} required={required} disabled={disabled} />
      </div>
    </div>
  );
};

export const FR_DATE_TYPES = new Set(["date", "datetime-local", "time", "month"]);

export const FrDateInput = ({ type, value, onChange, required, disabled, className, ...rest }) => {
  const testid = rest["data-testid"];
  const v = value || "";
  const wrap = (el) => <div className={cn("w-full", className)}>{el}</div>;
  if (type === "date") return wrap(<DatePart iso={v} onIso={(x) => emit(onChange, x)} testid={testid} required={required} disabled={disabled} />);
  if (type === "time") return wrap(<TimePart value={v} onValue={(x) => emit(onChange, x)} testid={testid} required={required} disabled={disabled} />);
  if (type === "month") return wrap(<MonthPart value={v} onValue={(x) => emit(onChange, x)} testid={testid} disabled={disabled} />);
  return wrap(<DateTimePart value={v} onChange={onChange} testid={testid} required={required} disabled={disabled} />);
};
